import ExportedImage from "next-image-export-optimizer";
import React, { useContext, useEffect, useRef, useState } from "react";
import { cn, handleLogOnReset } from "@/lib/utils";
import ButtonExit from "@/components/shared/futureChat/buttons/ButtonExit";
import store from "stores/store";
import { inject, observer } from "mobx-react";
import ChatMessageText from "@/components/chat/ChatMessageText";
import ChatMessageButton from "@/components/chat/ChatMessageButton";
import ChatMessageImage from "@/components/chat/ChatMessageImage";
import ChatMessageLink from "@/components/chat/ChatMessageLink";
import clsx from "clsx";
import generateChatMessages from "@/data/cameraAnimationScene";
import Timer from "@/components/shared/futureChat/chips/Timer";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage, getStrapiAssetUrl } from "utils/imageHelpers";
import CancelCall from "./CancelCall";
import Projection from "@/components/shared/projection/Projection";
import ConfettiEffect from "@/components/shared/effects/Konfetti";
import HeartEffect from "@/components/shared/effects/Hearts";
import LaserEffect from "@/components/shared/effects/Lasers";
import { motion } from "framer-motion";
import { ResetTrigger } from "@/components/enums/UiState";
import AvatarCam from "@/components/shared/webcam/AvatarCam";
import {
    ComponentChatMessageLink,
} from "types/strapi";
import {
    ComponentChatMessageButtonGroup as ComponentChatMessageButtonGroupLocal,
    ComponentChatMessageGoToBranch as ComponentChatMessageGoToBranchLocal,
    ComponentChatMessageImage as ComponentChatMessageImageLocal,
    ComponentChatMessageText as ComponentChatMessageTextLocal,
} from "types/chat";
import useGptConversation from "@/components/chat/hooks/gpt/useGptConversation";
import ChatTextInput from "@/components/chat/ChatTextInput";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import KeepHeightContainer from "@/components/web/chat/KeepHeightContainer";
import OpenLink from "./OpenLink";
import Over50 from "./Over50";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Image from "next/image";

type ChatProps = {
    onCancelCall: () => void;
    onReloadCall: () => void;
    className?: string;
    isOver50?: boolean;
    isByod?: boolean;
};

export type ComponentStoryChatMessageText = {
    __typename: "ComponentStoryChatMessageText";
    awaitingButtonId: number | undefined;
    delay: number;
    duration: number;
    id: number;
    text: string;
    isUser?: boolean;
    disableTitle: boolean;
};

export type ComponentStoryChatMessageTextButton = {
    __typename: "ComponentStoryChatMessageTextButton";
    awaitingButtonId: number | undefined;
    buttonId: number;
    buttonText: string;
    buttonTextTwo: string;
    buttonTextThree?: string;
    delay: number;
    duration: number;
    id: number;
};

export type SceneChatMessagesDynamicZone = ComponentStoryChatMessageText | ComponentStoryChatMessageTextButton;

const screenWidth = 2160;
const screenHeight = 4096;

const videoConstraints = {
    width: screenWidth,
    height: screenHeight,
    facingMode: "user",
};

type Answer = "green" | "2024" | "better" | null;
type Question = "green" | "2024" | "better" | null;

const Chat: React.FC<ChatProps> = ({ onCancelCall, onReloadCall, className, isOver50, isByod }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const {
        chatIndexCurrent,
        updateChatMessagesScene,
        chatSteleLocalized,
        globalLocalized,
        chatBranches,
        actualChatThread,
        updateActualChatThread,
        chatMessageEnlarged,
        chatProjectionSceneReady,
        selfieImageUrl,
        globalEventLocalized,
        chatEffect,
        chatTimer,
        updateChatTimer,
        resetAll,
        renderContent,
        loadingLocalized,
        isCancelCallVisible,
        updateIsCancelCallVisible,
        chatAiBranches,
        chatEventBranches,
        updateIsLoading,
        updateChatMessageEnlarged,
        chatbotLocalized,
        chatBranchesOver50,
    } = store;


    const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";
    const isEvent = isByod ? false : process.env.NEXT_PUBLIC_IS_EVENT === "true";

    const chatMessagesSceneRef = useRef<SceneChatMessagesDynamicZone[]>();
    const chatIndexCurrentRef = useRef<number>(0);
    const [selfieOpend, setSelfieOpend] = useState<boolean>(false)

    const { trackStartChat, trackEndChat, screenView } = useContext(TrackingContext);

    useEffect(() => {
        if (isOver50 || selfieImageUrl !== undefined) {
            setTimeout(() => {
                trackStartChat("chatstart", "under60");
                screenView("4.1 - Experience");

                displayingLastStateRef.current = false;
                const chatMessages = generateChatMessages();
                updateChatMessagesScene(chatMessages);
                chatMessagesSceneRef.current = chatMessages;

                // initialize actual chat thread with messages of root chat branch
                let rootName = "root";
                let rootGroup = "stele";

                if (isOnline && !isEvent) {
                    rootName = "ai-2:root";
                    rootGroup = "ai";

                    if (isOver50) {
                        rootName = "root";
                        rootGroup = "stele-over-50";
                    }
                } else if (isEvent) {
                    if (isOver50) {
                        rootName = "root";
                        rootGroup = "stele-over-50";
                    } else {
                        rootGroup = "event";
                    }

                }

                let root = chatBranches.find(
                    (el) => el?.attributes?.name === rootName && el?.attributes?.group === rootGroup,
                );

                let rootAi = chatAiBranches.find(
                    (el) => el?.attributes?.name === rootName && el?.attributes?.group === rootGroup,
                );

                let rootOver50 = chatBranchesOver50.find(
                    (el) => el?.attributes?.name === rootName && el?.attributes?.group === rootGroup,
                );

                let rootEvent = chatEventBranches.find(
                    (el) => el?.attributes?.name === rootName && el?.attributes?.group === rootGroup,
                );

                //let rootAi = root

                const now = new Date();
                let staggeredDelay = now.getTime();

                const nextBranchMessages =
                    root?.attributes?.chatMessages.map((e: any, i: any, array) => {
                        if (i === 0) {
                            staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                        } else {
                            staggeredDelay +=
                                (array[i - 1].parameters?.duration ?? 0) * 1000 + (e.parameters.delay ?? 0) * 1000;
                        }

                        return {
                            ...e,

                            timeToAppear: new Date(staggeredDelay),
                            isUser: false,
                            showSendersName: i === 0,
                        };
                    }) ?? [];

                const nextBranchMessagesAi: (
                    | ComponentChatMessageTextLocal
                    | ComponentChatMessageImageLocal
                    | ComponentChatMessageButtonGroupLocal
                    | ComponentChatMessageGoToBranchLocal
                    | ComponentChatMessageLink
                )[] =
                    rootAi?.attributes?.chatMessages.map((e, i, array) => {
                        if (i === 0) {
                            staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                        } else {
                            staggeredDelay +=
                                (array[i - 1].parameters?.duration ?? 0) * 1000 + (e.parameters?.delay ?? 0) * 1000;
                        }

                        return {
                            ...e,

                            timeToAppear: new Date(staggeredDelay),
                            isUser: false,
                            showSendersName: i === 0,
                        };
                    }) ?? [];

                const nextBranchMessagesOver50 =
                    rootOver50?.attributes?.chatMessages.map((e: any, i: any, array) => {
                        if (i === 0) {
                            staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                        } else {
                            staggeredDelay +=
                                (array[i - 1].parameters?.duration ?? 0) * 1000 + (e.parameters.delay ?? 0) * 1000;
                        }

                        return {
                            ...e,

                            timeToAppear: new Date(staggeredDelay),
                            isUser: false,
                            showSendersName: i === 0,
                        };
                    }) ?? [];

                const eventBranchMessages =
                    rootEvent?.attributes?.chatMessages.map((e: any, i: any, array) => {
                        if (i === 0) {
                            staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                        } else {
                            staggeredDelay +=
                                (array[i - 1].parameters?.duration ?? 0) * 1000 + (e.parameters.delay ?? 0) * 1000;
                        }

                        return {
                            ...e,

                            timeToAppear: new Date(staggeredDelay),
                            isUser: false,
                            showSendersName: i === 0,
                        };
                    }) ?? [];

                if (isOver50) {
                    updateActualChatThread(nextBranchMessagesOver50)
                }
                else if (isOnline && !isEvent && !isOver50) {
                    updateActualChatThread(nextBranchMessagesAi)
                }
                else if (isOnline && isEvent && !isOver50) {
                    updateActualChatThread(eventBranchMessages)
                }
                else {
                    updateActualChatThread(nextBranchMessages)
                }

            }, 2000);
        }
    }, [selfieImageUrl]);

    useEffect(() => {
        chatIndexCurrentRef.current = chatIndexCurrent;
    }, [chatIndexCurrent, actualChatThread]);

    const [showTimer, setShowTimer] = useState(false);
    const [count, setCount] = useState(chatSteleLocalized?.timeForCallTerminationMidChat);

    useEffect(() => {
        if (chatTimer) {
            const timeoutId = setTimeout(() => {
                setShowTimer(true);
            }, chatSteleLocalized?.timeoutAfterStillThereTillCallTerminationTimerStarts * 1000);

            return () => clearTimeout(timeoutId);
        } else {
            setShowTimer(false);
            resetTimer();
            return undefined;
        }
    }, [chatTimer]);

    useEffect(() => {
        if (chatEffect === "lastMessage") {
            setCount(chatSteleLocalized?.timeForCallTerminationEndChat);
            setShowTimer(true);
            trackEndChat("chatend");
            screenView("4.4 - Experience End");
        }
    }, [chatEffect]);

    const resetTimer = () => {
        setCount(chatSteleLocalized?.timeForCallTerminationMidChat);
        setShowTimer(false);
        updateChatTimer(false);
    };

    const handleReset = async () => {
        try {
            if (chatEffect === "lastMessage") {
                await handleLogOnReset(
                    renderContent,
                    actualChatThread[actualChatThread.length - 1],
                    ResetTrigger.ENDOFCALLTIMER,
                );
            } else {
                await handleLogOnReset(
                    renderContent,
                    actualChatThread[actualChatThread.length - 1],
                    ResetTrigger.CHATINACTIVETIMER,
                );
            }
        } catch (error) {
            console.error("Fehler beim Loggen des Events: ", error);
        }
        resetAll();
    };

    // useEffect(() => {
    //     if (showTimer) {
    //         const interval = setInterval(() => {
    //             setCount((prevCount: number) => {
    //                 if (prevCount > 0) {
    //                     return prevCount - 1;
    //                 } else {
    //                     try {
    //                         handleReset();
    //                     } catch (error) {
    //                         console.error("Fehler beim Loggen des Events: ", error);
    //                         resetAll();
    //                         return 0;
    //                     }
    //                 }
    //             });
    //         }, 1000);

    //         return () => clearInterval(interval);
    //     }
    // }, [showTimer, count]);

    useEffect(() => {
        console.log("-----------actualChatThread ", actualChatThread)
    }, [actualChatThread])

    const displayingLastStateRef = useRef<boolean>(false);

    const scrollRef = useRef<HTMLDivElement>(null);

    const disableLoadingBackground = () => {
        setTimeout(() => {
            updateIsLoading(false);
        }, 20000);
    };

    async function shareGif(gifUrl: string) {
        try {
            const response = await fetch(gifUrl);
            const blob = await response.blob();
            const file = new File([blob], "aged.gif", { type: "image/gif" });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: "My Future Portrait",
                    text: "Check out what I might look like in the future 👵🧓",
                });
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "aged.gif";
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error("Error while sharing GIF:", err);
        }
    }

    const [openSelfieGifOverlay, setOpenSelfieGifOverlay] = useState<boolean>(false)

    const openSelfieOverlay = () => {
        if (!selfieImageUrl) return;
        setOpenSelfieGifOverlay(true)
        setSelfieOpend(true)
    };

    const closeSelfieOverlay = () => {
        setOpenSelfieGifOverlay(false);
    };

    const { sendTextMessage } = useGptConversation(store);


    const textInputAnimationDelay = useRef<number>(0);

    const [isOpenLinkVisible, setOpenLinkVisible] = useState<boolean>(false);

    const onChatMessageLink = () => {
        setOpenLinkVisible(!isOpenLinkVisible);
    };

    return (
        <>
            <div
                className={cn(
                    "scrollbar-hidden relative flex flex-col items-center w-full h-full",
                    //"max-w-[768px]",
                    className,
                )}
            >
                {chatbotLocalized?.behindChatBg !== undefined && chatbotLocalized?.behindChatBg !== null && (
                    <div className={cn("top-0 absolute w-screen h-screen")}>


                        <ExportedImage
                            src={getImageUrlFromGeneralImage(chatbotLocalized?.behindChatBg)}
                            alt={getAltTextFromGeneralImage(chatbotLocalized?.behindChatBg)}
                            layout="fill"
                            objectFit="cover"
                            objectPosition="center"
                        />
                        <img
                            src="./Gradient_Petrol.png"
                            alt=""
                            className="bottom-0 left-0 absolute w-screen h-[75%] 3xs:h-[65%] 2xs:h-[60%] 4xs:h-[70%] xs:h-[55%] sm:h-0"
                        />

                    </div>
                )}
                {!isOnline && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: (chatMessageEnlarged || openSelfieGifOverlay) ? 0 : 1 }}
                        transition={{ duration: 0.1, delay: 0 }}
                        className="top-[84px] fixed rounded-[28px]"
                    >
                        <AvatarCam className={"h-[440px] w-[300px] rounded-[28px]"} rotate />
                    </motion.div>
                )}

                {!isOver50 && (
                    <div className="absolute w-full h-full pointer-events-none">
                        {chatEffect !== "lastMessage" && (
                            <div className="flex justify-center items-center md:bg-none w-full h-full">
                                <Projection />
                            </div>
                        )}
                    </div>
                )}


                {isOver50 && (
                    <div
                        className={clsx(
                            "absolute flex justify-center items-center w-full md:max-w-[calc(56.25vh-16px)] h-full Over50VideoBackground |",
                        )}
                    >
                        <video
                            autoPlay
                            muted
                            loop
                            className="absolute md:rounded-[16px] lg:rounded-[13px] 4xl:rounded-[17px] w-full h-full object-cover pointer-events-none"
                            playsInline
                            disablePictureInPicture
                        >
                            {chatSteleLocalized?.projectionBackgroundVideo.imageHD?.data?.attributes?.url !==
                                undefined && (
                                    <source
                                        src={getStrapiAssetUrl(
                                            chatSteleLocalized?.projectionBackgroundVideo.imageHD?.data?.attributes?.url,
                                        )}
                                        type="video/mp4"
                                    />
                                )}
                        </video>
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    className="absolute w-full h-full Chat:EndscreenBackground |"
                    animate={{
                        opacity: chatEffect === "lastMessage" ? 1 : 0,
                        scale: chatEffect === "lastMessage" ? 1 : 0,
                    }}
                    transition={{ duration: 0.2, delay: 0 }}
                >
                    <video
                        preload="auto"
                        width={2160}
                        height={3840}
                        autoPlay
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                        className="absolute w-full h-full object-cover pointer-events-none"
                    >
                        {loadingLocalized?.background.videoHD?.data?.attributes?.url !== undefined && (
                            <source
                                src={getStrapiAssetUrl(loadingLocalized?.background.videoHD?.data?.attributes?.url)}
                                type="video/mp4"
                            />
                        )}
                    </video>
                </motion.div>

                {chatEffect === "confetti" && <ConfettiEffect />}
                {chatEffect === "hearts" && <HeartEffect />}
                {chatEffect === "laser" && <LaserEffect />}

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: chatMessageEnlarged ? 0 : 1 }}
                    transition={{ duration: 0.1, delay: 0 }}
                    className={cn("Chat:UIOverlayHeader |", "md:max-w-[68vh] lg:max-w-[100vh] xl:max-w-[74vh]", {
                        "absolute top-0 flex w-full flex-col items-center justify-center px-4": isOnline,
                    })}
                >
                    <div className="flex justify-center w-full">
                        {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                            <div className={cn("top-0 relative w-[124px] h-[80px]", "2xs:h-[120px] 2xs:w-[186px]")}>
                                <ExportedImage
                                    src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                                    alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                                    layout="fill"
                                    objectFit="cover"
                                    objectPosition="center"
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={clsx(
                            "flex justify-between opacity-60 w-full",
                            isOnline
                                ? "absolute left-[0px] top-[34px] px-4 xl:top-[50px]"
                                : "absolute top-[250px] px-[128px]",
                        )}
                    >
                        <Timer />
                        {chatEffect === "lastMessage"
                            ? chatSteleLocalized?.endOfCallLoadingSpinner !== undefined &&
                            chatSteleLocalized?.endOfCallLoadingSpinner !== null && (
                                <div
                                    className={clsx(
                                        "relative",
                                        isOnline
                                            ? "h-[17px] w-[42px]"
                                            : "h-[90px] max-h-[90px] min-h-[90px] w-[236px] min-w-[236px] max-w-[236px]",
                                    )}
                                >
                                    <ExportedImage
                                        src={getImageUrlFromGeneralImage(chatSteleLocalized?.endOfCallLoadingSpinner)}
                                        alt={getAltTextFromGeneralImage(chatSteleLocalized?.endOfCallLoadingSpinner)}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center"
                                    />
                                </div>
                            )
                            : chatSteleLocalized?.loadingSpinner !== undefined &&
                            chatSteleLocalized?.loadingSpinner !== null && (
                                <div
                                    className={clsx(
                                        "relative",
                                        isOnline
                                            ? "h-[17px] w-[42px]"
                                            : "h-[90px] max-h-[90px] min-h-[90px] w-[236px] min-w-[236px] max-w-[236px]",
                                    )}
                                >
                                    <ExportedImage
                                        src={getImageUrlFromGeneralImage(chatSteleLocalized?.loadingSpinner)}
                                        alt={getAltTextFromGeneralImage(chatSteleLocalized?.loadingSpinner)}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center"
                                    />
                                </div>
                            )}
                    </div>
                    {isOnline && !isOver50 && (
                        <div className="top-16 xl:top-24 right-4 z-20 absolute flex flex-col gap-3">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: openSelfieGifOverlay ? 0 : 1 }}
                                transition={{ duration: 0.1 }}
                                className="rounded-[6px] w-[44px] 2xs:w-[67px] xl:w-[128px] h-[65px] 2xs:h-[98px] xl:h-[180px]"
                            >
                                <div className="after:block after:top-0 after:left-0 after:z-10 after:absolute relative after:shadow-[0_73px_91px_0px_rgba(0,0,0,0.05)] border-[1.5px] border-white rounded-[6px] after:w-full after:h-full overflow-hidden">
                                    <div className="after:block after:top-0 after:left-0 after:z-20 after:absolute relative after:shadow-[inset_-3px_3px_3px_0px_rgba(255,255,255,0.35)] after:w-full after:h-full">
                                        <div className="after:block after:top-0 after:left-0 after:z-20 after:absolute relative after:shadow-[inset_0px_-3px_3px_3px_rgba(255,255,255,0.35)] after:w-full after:h-full">
                                            <AvatarCam className="w-full h-full" />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {!!selfieImageUrl && (
                                <div className="flex flex-col justify-center items-center gap-[6px] max-w-[44px] 2xs:max-w-[67px] xl:max-w-[128px] text-center">
                                    <button
                                        type="button"
                                        onClick={openSelfieOverlay}
                                        className="relative 4k:border-[16px] rounded-[6px] w-[44px] 2xs:w-[67px] xl:w-[128px] h-[44px] 2xs:h-[67px] xl:h-[128px]"
                                    >
                                        {selfieOpend ?
                                            <>
                                                <div className="top-[-6px] left-[-6px] z-10 absolute">
                                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="2xs:w-[28px] 2xs:h-[28px]">
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22ZM14.4375 13.4063V12.2604L13.2917 11.1146V7.10419H13.8646V5.95836H8.13542V7.10419H8.70833V11.1146L7.5625 12.2604V13.4063H10.4271V16.8438L11 17.4167L11.5729 16.8438V13.4063H14.4375Z" fill="#D5D6D9" />
                                                    </svg>


                                                </div>
                                                <Image src={selfieImageUrl} alt="selfie gif" fill className="border border-white rounded-[6px] object-cover" />
                                            </>
                                            :

                                            <div className="flex justify-center items-center bg-grayShade-20 rounded-[6px] w-full h-full">
                                                <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3.85 38.5C2.79125 38.5 1.8849 38.123 1.13094 37.3691C0.376979 36.6151 0 35.7087 0 34.65V26.95H3.85V34.65H11.55V38.5H3.85ZM0 11.55V3.85C0 2.79125 0.376979 1.8849 1.13094 1.13094C1.8849 0.376979 2.79125 0 3.85 0H11.55V3.85H3.85V11.55H0ZM26.95 38.5V34.65H34.65V26.95H38.5V34.65C38.5 35.7087 38.123 36.6151 37.3691 37.3691C36.6151 38.123 35.7087 38.5 34.65 38.5H26.95ZM34.65 11.55V3.85H26.95V0H34.65C35.7087 0 36.6151 0.376979 37.3691 1.13094C38.123 1.8849 38.5 2.79125 38.5 3.85V11.55H34.65ZM19.25 19.25C17.6138 19.25 16.2422 18.6966 15.1353 17.5897C14.0284 16.4828 13.475 15.1112 13.475 13.475C13.475 11.8708 14.0284 10.5073 15.1353 9.38437C16.2422 8.26146 17.6138 7.7 19.25 7.7C20.8542 7.7 22.2177 8.26146 23.3406 9.38437C24.4635 10.5073 25.025 11.8708 25.025 13.475C25.025 15.1112 24.4635 16.4828 23.3406 17.5897C22.2177 18.6966 20.8542 19.25 19.25 19.25ZM7.7 30.8V27.1425C7.7 26.4688 7.86844 25.8351 8.20531 25.2416C8.54219 24.648 8.99937 24.1748 9.57687 23.8219C11.0527 22.9556 12.6007 22.2979 14.2209 21.8487C15.8411 21.3996 17.5175 21.175 19.25 21.175C20.9825 21.175 22.6589 21.3996 24.2791 21.8487C25.8993 22.2979 27.4473 22.9556 28.9231 23.8219C29.5006 24.1748 29.9578 24.648 30.2947 25.2416C30.6316 25.8351 30.8 26.4688 30.8 27.1425V30.8H7.7Z" fill="#E3E3E3" />
                                                </svg>
                                            </div>


                                        }
                                    </button>
                                    {!selfieOpend &&
                                        <p className="font-frutigerBold text-[12px] text-neutral-450">Selfie speichern</p>
                                    }
                                </div>
                            )}
                        </div>
                    )}

                    {isOnline && !isOver50 && openSelfieGifOverlay && (
                        <div
                            className={cn("z-[9999999999999999] fixed inset-0 flex justify-center bg-gray-700 px-[16px] overflow-hidden", className)}
                            onClick={closeSelfieOverlay}
                        >
                            <div className={cn("scrollbar-hidden z-[9999999] fixed flex justify-center px-[16px] w-full h-full overflow-hidden", className)}>
                                {globalEventLocalized?.selfieBackground !== undefined && globalEventLocalized?.selfieBackground !== null &&
                                    <ExportedImage
                                        src={getImageUrlFromGeneralImage(globalEventLocalized?.selfieBackground)}
                                        alt={getAltTextFromGeneralImage(globalEventLocalized?.selfieBackground)}
                                        layout="fill"
                                        objectFit="cover"
                                        objectPosition="center"
                                    />
                                }
                                <div className="relative flex flex-col justify-center items-center gap-[16px] w-full 2xs:w-[364px] h-full">
                                    <div className="flex">
                                        <p className="font-frutigerBold text-[16px] text-center leading-[115%]">Zum speichern <br /> gedrückt halten</p>
                                        <div className="right-0 absolute w-[36px] h-[36px]">
                                            <ButtonChat
                                                iconRight={
                                                    <ExportedImage
                                                        src={
                                                            globalEventLocalized?.selfieModalButton2.icon !== undefined &&
                                                                globalEventLocalized?.selfieModalButton2.icon !== null
                                                                ? getImageUrlFromGeneralImage(globalEventLocalized?.selfieModalButton2.icon)
                                                                : "No image provided"
                                                        }
                                                        alt={
                                                            globalEventLocalized?.selfieModalButton2.icon !== undefined &&
                                                                globalEventLocalized?.selfieModalButton2.icon !== null
                                                                ? getAltTextFromGeneralImage(globalEventLocalized?.selfieModalButton2.icon)
                                                                : "No image provided"
                                                        }
                                                        layout="fill"
                                                    />
                                                }
                                                variant="primary"
                                                onClick={closeSelfieOverlay}
                                            >
                                            </ButtonChat>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[8px] p-[8px] max-h-max">
                                        <div className="flex justify-center items-center w-full h-full min-h-[340px]" >
                                            {selfieImageUrl ?
                                                <ExportedImage
                                                    src={selfieImageUrl ?
                                                        (selfieImageUrl)
                                                        : process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_lena-cc.png"}
                                                    alt={"imgOld"}
                                                    fill={true}
                                                    className="relative rounded-[9px]"
                                                /> :
                                                chatSteleLocalized?.loadingSpinner !== undefined &&
                                                chatSteleLocalized?.loadingSpinner !== null && (
                                                    <div className="relative w-[42px] h-[17px]">
                                                        <ExportedImage
                                                            src={getImageUrlFromGeneralImage(chatSteleLocalized?.loadingSpinner)}
                                                            alt={getAltTextFromGeneralImage(chatSteleLocalized?.loadingSpinner)}
                                                            layout="fill"
                                                            objectFit="cover"
                                                            objectPosition="center"
                                                        />
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div>
                                    <div className="flex justify-center items-center gap-[8px] w-full">
                                        <div className="max-w-min whitespace-nowrap">
                                            <ButtonChat
                                                variant="primary"
                                                onClick={() => shareGif(selfieImageUrl ?? "")}
                                            >
                                                Teilen
                                            </ButtonChat>
                                        </div>
                                    </div>
                                </div>
                            </div >
                        </div>
                    )}

                </motion.div>
                {showTimer && chatEffect !== "lastMessage" && (
                    <div className="bottom-[1095px] absolute flex justify-center items-center opacity-70 w-full font-frutigerBold text-[50px] leading-[135%]">
                        {chatSteleLocalized?.callTerminatedTimerText} {count > 0 ? count : ""}
                    </div>
                )}
                <div
                    className={clsx(
                        "Chat:UIOverlay |",
                        "absolute flex flex-col justify-end",
                        //"max-w-[42.9375rem] lg:max-w-[50rem] h-full",
                        "h-full md:max-w-[68vh] lg:max-w-[100vh] xl:max-w-[74vh]",
                        "w-full px-4 2xs:px-6",
                    )}
                >
                    <div className={clsx("relative w-full h-[100%] md:h-full Chat:ScrollContainer |")}>
                        <div
                            ref={scrollRef}
                            className={clsx(
                                "group absolute flex flex-col-reverse",
                                "bottom-0 top-[0px] w-full",
                                "storyScrollbars overflow-y-auto overflow-x-hidden",
                                "transition-all duration-1000",
                                "pt-[250px] md:pt-[500px]",
                                true ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
                                showTimer && chatEffect !== "lastMessage" && "pb-[120px]",
                                "gap-1 pb-4",
                            )}
                        >
                            {chatProjectionSceneReady &&
                                actualChatThread &&
                                actualChatThread
                                    .map((value, index, all) => {
                                        /*if (index > chatIndexCurrent || !value) {
                                            return null;
                                        }*/

                                        let characterImageSrc = "";
                                        let characterImageAlt = "";
                                        let title = "";
                                        let text = "test";
                                        let imageSrc = "";
                                        let imageAlt = "";
                                        let buttonText = "";
                                        let buttonId = 0;
                                        let linkText = "";
                                        let linkUrl = "";
                                        let isUser = false;
                                        let disableTitle = true;

                                        //if(!appeared) return null
                                        /*
                                                                      if (
                                                                          value.__typename === "ComponentChatMessageText" ||
                                                                          value.__typename === "ComponentChatMessageButtonGroup"
                                                                      ) {
                                                                          if (value.delay !== undefined) {
                                                                              if (
                                                                                  index === chatIndexCurrent &&
                                                                                  chatIndexCurrent !== chatIndexDelayedCurrent
                                                                              ) {
                                                                                  return null;
                                                                              }
                                                                          }
                                                                          if (index === chatIndexCurrent && chatIndexCurrent !== chatIndexTypedCurrent) {
                                                                              return null;
                                                                          }
                                                                      }
                                      
                                                                      if (value.__typename === "ComponentChatMessageButtonGroup") {
                                                                          buttonText = ''//value.buttonText;
                                                                          buttonId = 0 //value.buttonId;
                                                                      }
                                      */

                                        if (value.__typename === "ComponentChatMessageText") {
                                            return (
                                                <ChatMessageText
                                                    key={"chatMessage-" + index}
                                                    characterImageSrc={characterImageSrc}
                                                    characterImageAlt={characterImageAlt}
                                                    title={title}
                                                    text={value?.text?.data?.attributes?.translation ?? ""}
                                                    isMainChatMessage={index === actualChatThread.length - 1}
                                                    switchBackground={value?.parameters?.switchBackground ?? undefined}
                                                    triggerEffect={value?.parameters?.triggerEffect ?? undefined}
                                                    timeToAppear={value?.timeToAppear}
                                                    isUser={value?.isUser ?? false}
                                                    disableTitle={!value?.showSendersName}
                                                    scrollRef={scrollRef}
                                                    typingDuration={value?.parameters?.duration ?? 0}
                                                    emoji={value.emoji ?? ""}
                                                />
                                            );
                                        } else if (value.__typename === "ComponentChatMessageAiText") {
                                            return (
                                                <ChatMessageText
                                                    key={"chatMessage-" + index}
                                                    characterImageSrc={characterImageSrc}
                                                    characterImageAlt={characterImageAlt}
                                                    title={title}
                                                    text={value.text}
                                                    isMainChatMessage={index === actualChatThread.length - 1}
                                                    switchBackground={value?.parameters?.switchBackground ?? undefined}
                                                    triggerEffect={value?.parameters?.triggerEffect ?? undefined}
                                                    timeToAppear={value?.timeToAppear}
                                                    isUser={value?.isUser ?? false}
                                                    disableTitle={!value?.showSendersName}
                                                    scrollRef={scrollRef}
                                                    typingDuration={value?.parameters?.duration ?? 0}
                                                    emoji={value.emoji ?? ""}
                                                />
                                            );
                                        } else if (value.__typename === "ComponentChatMessageButtonGroup") {
                                            // if (value.timeToAppear) {
                                            //     textInputAnimationDelay.current = Math.max(value?.timeToAppear.getTime() - new Date().getTime(), 0) / 1000;
                                            // } else {
                                            //     textInputAnimationDelay.current = 0;
                                            // }
                                            // console.log(textInputAnimationDelay.current);
                                            // return (
                                            //     <ChatMessageButton
                                            //         key={"chatMessage-" + index}
                                            //         characterImageSrc={characterImageSrc}
                                            //         characterImageAlt={characterImageAlt}
                                            //         title={""}
                                            //         text={""}
                                            //         isOver50={isOver50}
                                            //         buttonText={
                                            //             value?.buttons[0]?.label?.data?.attributes?.translation ?? ""
                                            //         }
                                            //         buttonTextTwo={
                                            //             value?.buttons[1]?.label?.data?.attributes?.translation ?? ""
                                            //         }
                                            //         buttonTextThree={
                                            //             value?.buttons[2]?.label?.data?.attributes?.translation ?? ""
                                            //         }
                                            //         buttonId={
                                            //             (isOnline
                                            //                 ? value?.buttons[0]?.chat_branch_ai.data?.id
                                            //                 : value?.buttons[0]?.chat_branch.data?.id) ?? ""
                                            //         }
                                            //         buttonIdTwo={
                                            //             (isOnline
                                            //                 ? value?.buttons[1]?.chat_branch_ai.data?.id
                                            //                 : value?.buttons[1]?.chat_branch.data?.id) ?? ""
                                            //         }
                                            //         buttonIdThree={
                                            //             (isOnline
                                            //                 ? value?.buttons[2]?.chat_branch_ai.data?.id
                                            //                 : value?.buttons[2]?.chat_branch.data?.id) ?? ""
                                            //         }
                                            //         buttons={value?.buttons}
                                            //         isMainChatMessage={index === actualChatThread.length - 1}
                                            //         timeToAppear={value?.timeToAppear}
                                            //         buttonEmoji={value?.buttons[0]?.emojiImg ?? null}
                                            //         buttonTwoEmoji={value?.buttons[1]?.emojiImg ?? null}
                                            //         buttonThreeEmoji={value?.buttons[2]?.emojiImg ?? null}
                                            //         buttonEmojiAlt={value?.buttons[0]?.emojiAlt ?? null}
                                            //         buttonTwoEmojiAlt={value?.buttons[1]?.emojiAlt ?? null}
                                            //         buttonThreeEmojiAlt={value?.buttons[2]?.emojiAlt ?? null}
                                            //         onClick={sendTextMessage}
                                            //     />
                                            // );
                                        } else if (value.__typename === "ComponentChatMessageImage") {
                                            return (
                                                <ChatMessageImage
                                                    key={"chatMessage-" + index}
                                                    characterImageSrc={characterImageSrc}
                                                    characterImageAlt={characterImageAlt}
                                                    title={title}
                                                    text={value.text?.data?.attributes?.translation ?? ""}
                                                    cta_enlarge={value.cta_enlarge?.data?.attributes?.translation ?? ""}
                                                    cta_close={value.cta_close?.data?.attributes?.translation ?? ""}
                                                    url={
                                                        value.dynamic_source === "selfie"
                                                            ? (selfieImageUrl ?? "")
                                                            : getStrapiAssetUrl(
                                                                value.image?.data?.attributes?.url ?? "",
                                                            )
                                                    }
                                                    /*    url={process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_lena-cc.png"}*/
                                                    altText={value?.altText?.data?.attributes?.translation ?? ""}
                                                    isMainChatMessage={index === actualChatThread.length - 1}
                                                    timeToAppear={value?.timeToAppear}
                                                    isUser={value?.isUser ?? false}
                                                    disableTitle={!value?.showSendersName}
                                                    scrollRef={scrollRef}
                                                    isSelfie={value.dynamic_source === "selfie"}
                                                    id={index}
                                                />
                                            );
                                        } else if (value.__typename === "ComponentChatMessageLink") {
                                            return (
                                                <ChatMessageLink
                                                    key={"chatMessage-" + index}
                                                    characterImageSrc={characterImageSrc}
                                                    characterImageAlt={characterImageAlt}
                                                    title={value.title?.data?.attributes?.translation ?? ""}
                                                    link_title={value.link_title?.data?.attributes?.translation ?? ""}
                                                    link_url={value.link_url?.data?.attributes?.translation ?? ""}
                                                    image_url={getStrapiAssetUrl(
                                                        value.image?.data?.attributes?.url ?? "",
                                                    )}
                                                    altText={value?.altText?.data?.attributes?.translation ?? ""}
                                                    isMainChatMessage={index === actualChatThread.length - 1}
                                                    timeToAppear={value?.timeToAppear}
                                                    isUser={value?.isUser ?? false}
                                                    disableTitle={!value?.showSendersName}
                                                    scrollRef={scrollRef}
                                                    onLink={onChatMessageLink}
                                                />
                                            );
                                        } else if (value.__typename === "ComponentChatMessageGoToBranch") {

                                            if (index === actualChatThread.length - 1) {
                                                const nextBranch = chatBranches.find(
                                                    (el) => el?.id === value?.chat_branch?.data?.id,
                                                );
                                                //const nextBranchMessages = nextBranch?.attributes?.chatMessages ?? []

                                                const now = new Date();
                                                let staggeredDelay = value?.timeToAppear?.getTime() ?? now.getTime();
                                                const nextBranchMessages =
                                                    nextBranch?.attributes?.chatMessages.map((e, i) => {
                                                        staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                                                        staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                                                        return {
                                                            ...e, // clone the template

                                                            timeToAppear: new Date(staggeredDelay),
                                                            isUser: false,
                                                            showSendersName: false,
                                                        };
                                                    }) ?? [];
                                                console.log("value ", value)
                                                console.log("value?.chat_branch_ai?.data?.id ", value?.chat_branch_ai?.data?.id)
                                                const nextBranchAi = chatAiBranches.find(
                                                    (el) => el?.id === value?.chat_branch_ai?.data?.id,
                                                );

                                                const nextBranchAiMessages =
                                                    nextBranchAi?.attributes?.chatMessages.map((e, i) => {
                                                        staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                                                        staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                                                        return {
                                                            ...e, // clone the template
                                                            timeToAppear: new Date(staggeredDelay),
                                                            isUser: false,
                                                            showSendersName: false,
                                                        };
                                                    }) ?? [];

                                                const nextBranchOver50 = chatBranchesOver50.find(
                                                    (el) => el?.id === value?.chat_branch_ai?.data?.id,
                                                );

                                                const nextBranchOver50Messages =
                                                    nextBranchOver50?.attributes?.chatMessages.map((e, i) => {
                                                        staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                                                        staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                                                        return {
                                                            ...e, // clone the template
                                                            timeToAppear: new Date(staggeredDelay),
                                                            isUser: false,
                                                            showSendersName: false,
                                                        };
                                                    }) ?? [];

                                                const nextBranchEvent = chatEventBranches.find(
                                                    (el) => el?.id === value?.chat_event?.data?.id,
                                                );

                                                const nextBranchEventMessages =
                                                    nextBranchEvent?.attributes?.chatMessages.map((e, i) => {
                                                        staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                                                        staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                                                        return {
                                                            ...e, // clone the template
                                                            timeToAppear: new Date(staggeredDelay),
                                                            isUser: false,
                                                            showSendersName: false,
                                                        };
                                                    }) ?? [];

                                                if (isEvent && nextBranchEvent?.attributes?.name) screenView(nextBranchEvent?.attributes?.name)
                                                else if (isOnline && isOver50 && nextBranchOver50?.attributes?.name) screenView(nextBranchOver50?.attributes?.name)
                                                else if (isOnline && nextBranchAi?.attributes?.name) screenView(nextBranchAi?.attributes?.name)

                                                // updateActualChatThread(
                                                //     isOnline
                                                //         ? isEvent
                                                //             ? actualChatThread.concat(nextBranchEventMessages)
                                                //             : isOver50
                                                //                 ? actualChatThread.concat(nextBranchOver50Messages)
                                                //                 : actualChatThread.concat(nextBranchAiMessages)
                                                //         : actualChatThread.concat(nextBranchMessages)
                                                // );
                                                if (isOver50) {
                                                    updateActualChatThread(actualChatThread.concat(nextBranchOver50Messages))
                                                }
                                                else if (isOnline && isEvent && !isOver50) {
                                                    updateActualChatThread(actualChatThread.concat(nextBranchEventMessages))
                                                }
                                                if (isOnline && !isEvent && !isOver50) {
                                                    if (nextBranchAiMessages.length > 0) {
                                                        updateActualChatThread(actualChatThread.concat(nextBranchAiMessages));
                                                    }
                                                }
                                                else {
                                                    updateActualChatThread(actualChatThread.concat(nextBranchMessages))
                                                }
                                            }
                                            return null;
                                        }
                                    })
                                    .reverse()}
                        </div>
                    </div>
                    {chatEffect !== "lastMessage" && (
                        <KeepHeightContainer>
                            {chatProjectionSceneReady &&
                                actualChatThread &&
                                actualChatThread.map((value, index) => {
                                    let characterImageSrc = "";
                                    let characterImageAlt = "";
                                    if (value.__typename === "ComponentChatMessageButtonGroup") {
                                        console.log("value in Button Group (buttons are triggerable) ", value)
                                        console.log("value?.buttons[0]?.chat_branch_ai.data?.id ", value?.buttons[0]?.chat_branch_ai.data?.id)
                                        console.log("value?.buttons[0]?.chat_event.data?.id ", value?.buttons[0]?.chat_event.data?.id)
                                        if (value.timeToAppear) {
                                            textInputAnimationDelay.current =
                                                Math.max(value?.timeToAppear.getTime() - new Date().getTime(), 0) /
                                                1000;
                                        } else {
                                            textInputAnimationDelay.current = 0;
                                        }
                                        return (
                                            <ChatMessageButton
                                                key={"chatMessage-" + index}
                                                characterImageSrc={characterImageSrc}
                                                characterImageAlt={characterImageAlt}
                                                title={""}
                                                text={""}
                                                isOver50={isOver50}
                                                buttonText={
                                                    value?.buttons[0]?.label?.data?.attributes?.translation ?? ""
                                                }
                                                buttonTextTwo={
                                                    value?.buttons[1]?.label?.data?.attributes?.translation ?? ""
                                                }
                                                buttonTextThree={
                                                    value?.buttons[2]?.label?.data?.attributes?.translation ?? ""
                                                }
                                                buttonId={
                                                    (isOnline
                                                        ? value?.buttons[0]?.chat_branch_ai.data?.id
                                                        : value?.buttons[0]?.chat_branch_ai.data?.id) ?? ""
                                                }
                                                buttonIdTwo={
                                                    (isOnline
                                                        ? value?.buttons[1]?.chat_branch_ai.data?.id
                                                        : value?.buttons[1]?.chat_branch_ai.data?.id) ?? ""
                                                }
                                                buttonIdThree={
                                                    (isOnline
                                                        ? value?.buttons[2]?.chat_branch_ai.data?.id
                                                        : value?.buttons[2]?.chat_branch_ai.data?.id) ?? ""
                                                }
                                                buttons={value?.buttons}
                                                isMainChatMessage={index === actualChatThread.length - 1}
                                                timeToAppear={value?.timeToAppear}
                                                buttonEmoji={value?.buttons[0]?.emojiImg ?? null}
                                                buttonTwoEmoji={value?.buttons[1]?.emojiImg ?? null}
                                                buttonThreeEmoji={value?.buttons[2]?.emojiImg ?? null}
                                                buttonEmojiAlt={value?.buttons[0]?.emojiAlt ?? null}
                                                buttonTwoEmojiAlt={value?.buttons[1]?.emojiAlt ?? null}
                                                buttonThreeEmojiAlt={value?.buttons[2]?.emojiAlt ?? null}
                                                onClick={sendTextMessage}
                                                isByod={isByod}
                                            />
                                        );
                                    }
                                })}
                        </KeepHeightContainer>
                    )}
                    {chatEffect === "lastMessage" && (
                        <div className="flex flex-col items-center gap-2 py-4 w-full Chat:LastMessage |">
                            <div className="font-frutigerBold text-xs italic">
                                {chatSteleLocalized?.connectionLostEndOfChat}
                            </div>
                            {/* <div className="font-frutigerBold text-xs italic">
                                {chatSteleLocalized?.callTerminatedTimerText} {count > 0 ? count : ""}
                            </div> */}
                        </div>
                    )}
                    <div className="flex flex-row justify-center items-center gap-1 pb-6 2xs:pb-9 w-full">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: chatMessageEnlarged ? 0 : 1 }}
                            transition={{ duration: 0.1, delay: 0 }}
                            className=""
                        >
                            <ButtonExit onClick={onCancelCall} className={clsx(chatEffect !== "lastMessage" ? "h-[38px] w-[38px] 2xs:h-[53px] 2xs:w-[53px]" : "h-[50px] w-[50px] rounded-[12px] 2xs:rounded-[18px] 2xs:h-[65px] 2xs:w-[65px]")}>
                                <ExportedImage
                                    src={
                                        chatSteleLocalized?.button.icon !== undefined &&
                                            chatSteleLocalized?.button.icon !== null
                                            ? getImageUrlFromGeneralImage(chatSteleLocalized.button.icon)
                                            : "No image provided"
                                    }
                                    alt={
                                        chatSteleLocalized?.button.icon !== undefined &&
                                            chatSteleLocalized?.button.icon !== null
                                            ? getAltTextFromGeneralImage(chatSteleLocalized.button.icon)
                                            : "No image provided"
                                    }
                                    layout="fill"
                                />
                            </ButtonExit>
                        </motion.div>

                        {chatEffect !== "lastMessage" &&
                            <ChatTextInput
                                className="z-10"
                                visible={true}
                                animationDelay={textInputAnimationDelay.current}
                                // disabled={inputDisabled}
                                onSubmit={sendTextMessage}
                            />
                        }
                    </div>
                </div>
            </div >

            {isOpenLinkVisible && (
                <div
                    className={cn("top-0 left-0 z-20 absolute w-screen h-full", [
                        !isOpenLinkVisible && "pointer-events-none opacity-0",
                        isOpenLinkVisible && "pointer-events-auto opacity-100",
                    ])}
                >
                    <OpenLink onGoBack={() => setOpenLinkVisible(false)} />
                </div>
            )
            }
            {
                isCancelCallVisible && (
                    <div
                        className={cn("top-0 left-0 z-20 absolute w-screen h-full", [
                            !isCancelCallVisible && "pointer-events-none opacity-0",
                            isCancelCallVisible && "pointer-events-auto opacity-100",
                        ])}
                    >
                        <CancelCall
                            onCancelCall={onCancelCall}
                            onGoBack={() => updateIsCancelCallVisible(false)}
                            onReloadCall={onReloadCall}
                        />
                    </div>
                )
            }
        </>
    );
};

export default inject("store")(observer(Chat));
